'use client'

import { useState } from 'react'
import { useTranslations } from '@/lib/i18n'
import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  HelpCircle,
  Search,
  Sparkles,
  ChevronRight,
  Loader2,
  BookOpen
} from 'lucide-react'
import { cn } from '@/lib/utils'
import helpData from '@/lib/help-data.json'
import { MarkdownPreview } from '@/components/ui/markdown-preview'
import { useAuthStore } from '@/lib/stores/auth-store'

interface HelpSidebarContentProps {
  isOpen: boolean
  onClose: () => void
}

interface AIHelpResponse {
  answer: string
  relatedPages: Array<{
    title: string
    path: string
    description: string
  }>
  relatedFeatures: Array<{
    title: string
    description: string
  }>
  confidence: number
}

export function HelpSidebarContent({ isOpen, onClose }: HelpSidebarContentProps) {
  const t = useTranslations('help')
  const c = useTranslations('common')
  const { session, user } = useAuthStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null)
  const [aiResponse, setAiResponse] = useState<AIHelpResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  })

  // Prepare context data for AI - filter relevant data based on keywords
  const prepareContextForAI = (query: string) => {
    const keywords = query.toLowerCase().split(/\s+/).filter(k => k.length > 2)
    
    // Filter relevant features
    const relevantFeatures = helpData.features.filter(feature =>
      keywords.some(keyword =>
        feature.title.toLowerCase().includes(keyword) ||
        feature.description.toLowerCase().includes(keyword) ||
        feature.capabilities.some(cap => cap.toLowerCase().includes(keyword)) ||
        feature.category.toLowerCase().includes(keyword)
      )
    )

    // Filter relevant pages
    const relevantPages = helpData.sitemap.filter(page =>
      keywords.some(keyword =>
        page.title.toLowerCase().includes(keyword) ||
        page.description.toLowerCase().includes(keyword) ||
        page.path.toLowerCase().includes(keyword)
      )
    )

    // Filter relevant FAQs
    const relevantFAQs = helpData.faq.filter(faq =>
      keywords.some(keyword =>
        faq.question.toLowerCase().includes(keyword) ||
        faq.answer.toLowerCase().includes(keyword) ||
        faq.keywords.some(k => k.toLowerCase().includes(keyword))
      )
    )

    return {
      query,
      keywords,
      features: relevantFeatures.length > 0 ? relevantFeatures : helpData.features.slice(0, 5),
      pages: relevantPages.length > 0 ? relevantPages : helpData.sitemap.slice(0, 5),
      faqs: relevantFAQs.length > 0 ? relevantFAQs : helpData.faq.slice(0, 5),
      allFeatures: helpData.features.map(f => ({ title: f.title, category: f.category, path: f.relatedPages?.[0] })),
      allPages: helpData.sitemap.map(p => ({ title: p.title, path: p.path }))
    }
  }

  // Handle AI search
  const handleAISearch = async () => {
    if (!searchQuery.trim()) return

    // Check if user is logged in
    if (!user || !session) {
      setError('Please log in to use AI-powered help search.')
      return
    }

    setLoading(true)
    setError(null)
    setAiResponse(null)

    try {
      const context = prepareContextForAI(searchQuery)
      const headers = getAuthHeaders()
      
      // Use existing AI assistant endpoint
      const response = await fetch('/api/assistant/ai', {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          userMessage: searchQuery,
          context: {
            userId: user.id?.toString() || 'guest',
            userData: {
              assignedCount: 0,
              overdueCount: 0,
              completedToday: 0,
              activeProjects: []
            },
            helpData: {
              features: context.features,
              pages: context.pages,
              faqs: context.faqs,
              allFeatures: context.allFeatures,
              allPages: context.allPages
            }
          },
          assistantType: 'help',
          source: 'help' // Not a chat source, so won't trigger Slack
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Search failed: ${response.status}`)
      }

      const data = await response.json()
      
      // Extract related pages and features from AI response
      const aiAnswer = data.response || data.answer || 'No response received'
      
      // Parse markdown links from response: [Title](path)
      const relatedPages: Array<{ title: string; path: string; description: string }> = []
      const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
      let match
      while ((match = linkRegex.exec(aiAnswer)) !== null) {
        const [, title, path] = match
        // Find description from context
        const pageInfo = context.pages.find(p => p.path === path)
        relatedPages.push({
          title,
          path,
          description: pageInfo?.description || ''
        })
      }
      
      // If no links found, use top relevant pages from context
      if (relatedPages.length === 0 && context.pages.length > 0) {
        relatedPages.push(...context.pages.slice(0, 3))
      }
      
      setAiResponse({
        answer: aiAnswer,
        relatedPages,
        relatedFeatures: context.features.slice(0, 2).map(f => ({
          title: f.title,
          description: f.description
        })),
        confidence: data.confidence || 0.8
      })
    } catch (err) {
      console.error('AI help search error:', err)
      setError(err instanceof Error ? err.message : 'Failed to get help. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleAISearch()
    }
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    setAiResponse(null)
    setError(null)
  }

  return (
    <GlobalSidebar
      isOpen={isOpen}
      onClose={onClose}
      title={t('title')}
      subtitle={t('subtitle')}
      icon={<HelpCircle className="h-6 w-6" />}
      width="lg"
    >
      <div className="space-y-6">
        {/* AI Search Section */}
        <div className="space-y-3">
          <div className="relative">
            <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-primary" />
            <Input
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              className="h-12 pl-11 pr-24 text-base shadow-sm"
              disabled={loading}
            />
            {searchQuery && (
              <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSearch}
                  className="h-8"
                  disabled={loading}
                >
                  {c('close')}
                </Button>
                <Button
                  size="sm"
                  onClick={handleAISearch}
                  disabled={loading || !searchQuery.trim()}
                  className="h-8"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            {t('aiSearchHint')}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="p-4">
              <p className="text-sm text-destructive">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* AI Response */}
        {aiResponse && (
          <Card className="border-0 bg-gradient-to-br from-primary/5 to-purple-500/5 shadow-none">
            <CardContent className="p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="rounded-lg bg-primary/10 p-2 flex-shrink-0">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    AI Assistant Response
                    {aiResponse.confidence > 0 && (
                      <span className="text-xs text-muted-foreground">
                        ({Math.round(aiResponse.confidence * 100)}% confidence)
                      </span>
                    )}
                  </h4>
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <MarkdownPreview content={aiResponse.answer} />
                  </div>
                </div>
              </div>

              {/* Related Pages - HIDDEN per user request */}
              {/* {aiResponse.relatedPages && aiResponse.relatedPages.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                    📍 Related Pages:
                  </p>
                  <div className="space-y-1">
                    {aiResponse.relatedPages.map((page, index) => (
                      <a
                        key={index}
                        href={page.path}
                        onClick={onClose}
                        className="flex items-center gap-2 text-sm text-primary hover:underline p-2 rounded hover:bg-primary/5 transition-colors"
                      >
                        <ChevronRight className="h-4 w-4 flex-shrink-0" />
                        <span className="font-medium">{page.title}</span>
                        <span className="text-xs text-muted-foreground">- {page.description}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )} */}

              {/* Related Features - HIDDEN per user request */}
              {/* {aiResponse.relatedFeatures && aiResponse.relatedFeatures.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                    ✨ Related Features:
                  </p>
                  <div className="space-y-2">
                    {aiResponse.relatedFeatures.map((feature, index) => (
                      <div key={index} className="text-sm p-2 rounded bg-muted/30">
                        <p className="font-medium">{feature.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{feature.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )} */}
            </CardContent>
          </Card>
        )}

        {/* Default FAQ Section - Only show when no search results */}
        {!aiResponse && !loading && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2">
              <HelpCircle className="h-4 w-4 text-primary" />
              <h3 className="font-semibold text-sm uppercase tracking-wide">
                {t('frequentlyAsked')}
              </h3>
            </div>

            <div className="space-y-2">
              {helpData.faq.slice(0, 8).map((faq) => (
                <Card 
                  key={faq.id} 
                  className={cn(
                    "hover:shadow-md transition-all cursor-pointer",
                    expandedFaq === faq.id && "ring-2 ring-primary/50"
                  )}
                >
                  <CardContent className="p-4">
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                      className="w-full text-left flex items-start gap-3"
                    >
                      <ChevronRight className={cn(
                        "h-5 w-5 text-primary flex-shrink-0 transition-transform mt-0.5",
                        expandedFaq === faq.id && "rotate-90"
                      )} />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm mb-1 text-foreground">
                          {faq.question}
                        </h4>
                        {expandedFaq === faq.id && (
                          <p className="text-sm text-foreground/80 mt-2 leading-relaxed">
                            {faq.answer}
                          </p>
                        )}
                      </div>
                    </button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </GlobalSidebar>
  )
}
